/**
 * `theme` (bolsa plana, compatible con theme-silia-antiguo) y `palette`
 * (los mismos tokens como `var()`).
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9).
 */

import {
  brand,
  surface,
  ink,
  line,
  semantic,
  tagExtras,
  emotion,
  kind,
  node,
  fill,
  elevation,
  typography,
  radius,
  space,
  control,
  tagPalette,
  silliaLogo,
  silliaLogoMobile,
  silliaBackground,
  silliaBackgroundWebp,
  silliaHeroBackground,
  silliaHeroBackgroundWebp,
  silliaHeroBackgroundWebpMobile,
  silliaModulesBackground,
  silliaModulesBackgroundWebp,
} from './primitives';

/* ═══════════════════════════════════════════════════════════════════════
   4. `theme` — the flat object, back-compatible with theme-silia-antiguo
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * The flat token bag. Every key the old theme published is still here with the
 * same name, so a module can be migrated one at a time rather than all at once
 * — but the values now come from the primitives above, which means several of
 * them changed:
 *
 *   · `colorText` was #000000, now #3c3c48. Pure black is not in the palette;
 *     the prototype's audit calls it out as the single most-copied wrong value.
 *   · `colorTextDescription` was rgba(0,0,0,0.60), now the flat #6a6a73 that
 *     passes AA on all three surfaces. Alpha text compounds when a description
 *     sits on a tinted row.
 *   · `colorBorder` / `colorSplit` were alpha, now flat hex, for the same reason.
 *   · `colorPrimary` was #E3F571, now #deff38 — the real brand lime. #E3F571 is
 *     a washed copy that predates the DS.
 *
 * These are deliberate corrections, not drift. A module that looks different
 * after migrating is showing you a value that was wrong before.
 */
export const theme = {
  /* ── Brand ─────────────────────────────────────────────────────────── */
  colorPrimary: brand.lime,
  colorSecondary: brand.n700,
  colorTertiary: brand.n400,
  colorAvatar: brand.n200,
  colorIcon: brand.n700,
  colorBrandBlack: brand.black,
  colorPurple2: brand.n100,
  colorPurple3: brand.n200,
  colorPurple4: brand.n400,
  colorPurple5: brand.n500,
  colorPurple6: brand.n600,
  colorPurple7: brand.n700,
  colorPurple8: brand.n800,
  colorPurple9: brand.n900,

  /* ── Surfaces ──────────────────────────────────────────────────────── */
  layoutBg: surface.bgLayout,
  colorBgContainer: surface.bgContainer,
  colorBgElevated: surface.bgElevated,
  colorSurfaceTint1: surface.tint1,
  colorSurfaceTint2: surface.tint2,
  colorSurfaceTint3: surface.tint3,
  colorInputAddon: surface.inputAddon,
  colorBgDisabled: surface.tint3,

  /* ── Text & icons ──────────────────────────────────────────────────── */
  colorText: ink.text,
  colorTextPrimary: ink.text,
  colorTextDescription: ink.textDescription,
  colorTextSecondary: ink.textDescription,
  colorTextIcon: ink.textIcon,
  colorTextPlaceholder: ink.textPlaceholder,
  colorTextInverse: ink.textInverse,
  /** Text on the lime CTA — brand black, never white. */
  colorTextButton: brand.black,

  /* ── Borders ───────────────────────────────────────────────────────── */
  colorBorder: line.border,
  colorBorderSecondary: line.borderSubtle,
  colorBorderSubtle: line.borderSubtle,
  colorSplit: line.split,
  colorFillContent: line.fillContent,

  /* ── Translucent fills ─────────────────────────────────────────────── */
  colorFillQuaternary: fill.f1,
  colorFillTertiary: fill.f2,
  colorFillSecondary: fill.f3,
  colorBgTextHover: fill.f2,
  colorFillBrand1: fill.brand1,
  colorFillBrand2: fill.brand2,
  colorFillInverse1: fill.inverse1,
  colorFillInverse2: fill.inverse2,
  colorScrim: fill.scrim,
  colorScrimStrong: fill.scrimStrong,
  colorLimeSoft: fill.limeSoft,
  colorLimeBorder: fill.limeBorder,
  colorLimeTenue: fill.limeTenue,

  /* ── Semantic ──────────────────────────────────────────────────────── */
  ...semantic,
  /** Old names, mapped onto the semantic scale. `colorStatusUnknown` was an
      orange invented outside the palette; it is the warning tone. */
  colorError: semantic.dangerFg,
  colorErrorBg: semantic.dangerBg,
  colorStatusUnknown: semantic.warningFg,
  colorStatusUnknownBg: semantic.warningBg,
  colorStatusUnknownBorder: semantic.warningBd,

  /* ── Emotion, kind, node ───────────────────────────────────────────── */
  emotion,
  kind,
  node,

  /* ── Elevation ─────────────────────────────────────────────────────── */
  shadowSM: elevation.sm,
  shadowMD: elevation.md,
  shadowLG: elevation.lg,
  shadowXL: elevation.xl,
  focusRing: elevation.focusRing,
  shadowGlow: elevation.glow,
  shadowCta: elevation.cta,
  shadowCtaHover: elevation.ctaHover,
  /** Old card-shadow names — now the sm/md steps of the real scale. */
  colorCardShadow: elevation.sm,
  colorCardShadowHover: elevation.md,

  /* ── Typography ────────────────────────────────────────────────────── */
  fontFamily: typography.fontSans,
  fontFamilyMono: typography.fontMono,
  fontSize: typography.fontSize,
  fontSizeIcons: typography.fontSizeIcons,
  lineHeight: typography.lineHeight,

  /* ── Shape ─────────────────────────────────────────────────────────── */
  borderRadiusXS: radius.xs,
  borderRadiusSM: radius.sm,
  borderRadius: radius.md,
  borderRadiusLG: radius.lg,
  borderRadiusPill: radius.pill,
  borderRadiusCircle: radius.circle,

  /* ── Space ─────────────────────────────────────────────────────────── */
  paddingXXS: space.xxs,
  paddingXS: space.xs,
  paddingSM: space.sm,
  padding: space.md,
  paddingLG: space.lg,
  paddingXL: space.xl,
  paddingXXL: space.xxl,
  marginXXS: space.xxs,
  marginXS: space.xs,
  marginSM: space.sm,
  margin: space.md,
  marginLG: space.lg,
  marginXL: space.xl,
  marginXXL: space.xxl,
  gapXS: space.xs,
  gapSM: space.sm,
  gapMD: space.md,
  gapLG: space.xl,
  gapXL: space.xxl,

  /* ── Controls ──────────────────────────────────────────────────────── */
  controlHeight: control.height,
  controlHeightSM: control.heightSM,

  /* ── Tag palette ───────────────────────────────────────────────────── */
  tagPalette,

  /* ── Assets & identity ─────────────────────────────────────────────── */
  appName: 'Silia',
  logoSrc: silliaLogo,
  logoUrl: `url(${silliaLogo})`,
  logoMobileUrl: `url(${silliaLogoMobile})`,
  backgroundUrl: `url(${silliaBackground})`,
  backgroundUrlWebp: `url(${silliaBackgroundWebp})`,
  heroBackgroundUrl: `url(${silliaHeroBackground})`,
  heroBackgroundUrlWebp: `url(${silliaHeroBackgroundWebp})`,
  heroBackgroundSrc: silliaHeroBackground,
  heroBackgroundSrcWebp: silliaHeroBackgroundWebp,
  heroBackgroundSrcWebpMobile: silliaHeroBackgroundWebpMobile,
  modulesBackgroundUrl: `url(${silliaModulesBackground})`,
  modulesBackgroundUrlWebp: `url(${silliaModulesBackgroundWebp})`,
  heroTextColor: surface.inputAddon,
  colorTitleLogin: '#ECECDB',
  colorIconLogin: '#ECECDB',

  /* ── Surfaces with a name of their own ─────────────────────────────────
     Chrome that is not a plain card: the top bar, an assistant badge, the
     inbox icon. Each is a purple-ramp step, so they follow the template. */
  colorTopBarBg: brand.n100,
  colorTopBarBorder: line.borderSubtle,
  colorTopBarText: ink.text,
  colorTopBarBadgeBg: brand.n200,
  colorTopBarBadgeBorder: brand.n500,
  colorTopBarBadgeText: brand.n700,
  colorTopBarReturnHover: brand.n700,
  colorAssistantBadgeBg: brand.n400,
  colorAssistantBadgeText: brand.n700,
  colorAssistantBadgeBorder: brand.n500,
  colorIconInbox: ink.textIcon,
  colorIconWidget: brand.n800,
  colorCardHover: surface.tint3,
  colorBgTag: brand.n100,
  colorTagText: brand.n700,
  colorTagBorder: brand.n500,
  colorPrimaryBorderHover: fill.limeBorder,
  colorPrimaryBgHover: fill.limeSoft,

  /* ── Account / profile ─────────────────────────────────────────────── */
  colorAccountTextStrong: ink.text,
  colorAccountTextMuted: ink.textIcon,
  colorAccountBorderSubtle: line.borderSubtle,
  colorAccountBgElevated: surface.bgElevated,
  colorAccountBgPanel: surface.inputAddon,

  /* ── Neutral ramp (flat-hex counterparts, kept as aliases) ─────────── */
  colorNeutralTextDescription: ink.textDescription,
  colorNeutralTextPlaceholder: ink.textPlaceholder,
  colorNeutralBorder: line.border,
  colorNeutralSurfaceTint: surface.tint1,
  colorNeutralSurfaceTintStrong: surface.tint3,

  /* ── Home — object grid & cards ────────────────────────────────────── */
  colorHomeBgLayout: surface.bgLayout,
  colorHomeBgCard: surface.bgContainer,
  colorHomeSplit: line.split,
  colorHomeBorder: line.border,
  colorHomeText: ink.text,
  colorHomeTextDescription: ink.textDescription,
  colorHomeTextIcon: ink.textIcon,
  /* Acento y primario del namespace --ant-*, con nombre de rol.
     Reemplazaron a los colorHomePurple-N, colorHomeLime y colorHomeBrandBlack:
     el prefijo Home tampoco describía nada — estos tokens se leen desde
     agent-skills, account y billing, no desde Home. */
  colorAccentBgSubtle: brand.n100,
  colorAccentBg: brand.n200,
  colorAccentBorder: brand.n400,
  colorAccentBorderStrong: brand.n500,
  colorAccentFg: brand.n700,
  colorAccentFgStrong: brand.n800,
  colorPrimaryBrand: brand.lime,
  colorInkStrong: brand.black,
  colorHomeFillContent: line.fillContent,
  colorHomeCardShadow: elevation.sm,
  colorHomeCardShadowHover: elevation.md,
  colorHomeEmptyPrimary: brand.n100,
  colorHomeEmptyPrimaryBorder: brand.n400,
  colorHomeEmptyCtaBg: brand.lime,
  colorHomeEmptyCtaQuietBg: fill.f2,
  colorHomeEmptyCtaQuietHover: brand.n200,
  colorHomeEmptyTemplateHover: brand.n200,
  colorHomeBannerGradient:
    'radial-gradient(ellipse 60% 80% at 30% 100%, rgba(91,81,140,0.55) 0%, transparent 70%), radial-gradient(ellipse 70% 80% at 70% 100%, rgba(140,100,170,0.4) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 20% 0%, rgba(255,180,200,0.65) 0%, transparent 65%), radial-gradient(ellipse 40% 50% at 85% 5%, rgba(180,220,230,0.45) 0%, transparent 70%), radial-gradient(circle 100px at 75% 30%, rgba(227,245,113,0.55) 0%, transparent 80%), linear-gradient(180deg, #e8d5e8 0%, #c5b1d8 60%, #9d8bbf 100%)',

  /* ── Metrics & charts ──────────────────────────────────────────────────
     The purple ramp read as a sequence. These were five hand-picked violets
     that did not match any step of the scale. */
  colorMetricsBarFill: brand.n400,
  colorMetricsLine: brand.n500,
  colorMetricsEscalations: brand.n700,
  colorMetricsMessages: brand.n400,
  colorMetricsCategory1: brand.n700,
  colorMetricsCategory2: brand.n400,
  colorMetricsCategory3: brand.n500,
  colorMetricsCategory4: brand.n600,
  colorMetricsCategory5: brand.n800,
  chartTinyStroke: brand.n700,
  chartTinyFill: `l(270) 1:${brand.lime} 0:${fill.limeSoft}`,
  chartTinyBrandColor: fill.limeSoft,
  chartLineColors: [brand.n700, brand.n500],
  chartPieColors: [brand.n800, brand.n600, brand.n400],
  chartFunnelColors: [brand.n200, brand.n400, brand.n500, brand.n600, brand.n700],
  chartLabelColor: ink.text,
} as const;

/* ═══════════════════════════════════════════════════════════════════════
   5. `palette` — CSS-variable-backed tokens, for inline styles
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Every token as a `var(--silia-*)` string rather than a literal.
 *
 * Use this — not `theme` — anywhere the value ends up in a React `style` prop
 * or an AntD component prop, because a `var()` re-resolves when the active
 * template changes the body class, while a literal hex freezes at render.
 *
 * The one place you cannot use it is where the browser needs a real colour:
 * `<input type="color">`, `<canvas>`, and chart libraries that parse the string
 * themselves. Those read from `theme` instead.
 */
export const palette = {
  inkStrong: 'var(--silia-ink-strong)',
  primary: 'var(--silia-primary)',
  accentBgSubtle: 'var(--silia-accent-bg-subtle)',
  accentBg: 'var(--silia-accent-bg)',
  accentBorder: 'var(--silia-accent-border)',
  accentBorderStrong: 'var(--silia-accent-border-strong)',
  accentFgMuted: 'var(--silia-accent-fg-muted)',
  accentFg: 'var(--silia-accent-fg)',
  accentFgStrong: 'var(--silia-accent-fg-strong)',
  accentSolid: 'var(--silia-accent-solid)',

  bgLayout: 'var(--silia-bg-layout)',
  bgElevated: 'var(--silia-bg-elevated)',
  bgContainer: 'var(--silia-bg-container)',
  surfaceTint1: 'var(--silia-surface-tint-1)',
  surfaceTint2: 'var(--silia-surface-tint-2)',
  surfaceTint3: 'var(--silia-surface-tint-3)',
  inputAddon: 'var(--silia-input-addon)',

  text: 'var(--silia-text)',
  textDescription: 'var(--silia-text-description)',
  textIcon: 'var(--silia-text-icon)',
  textPlaceholder: 'var(--silia-text-placeholder)',
  textInverse: 'var(--silia-text-inverse)',

  border: 'var(--silia-border)',
  borderSubtle: 'var(--silia-border-subtle)',
  fillContent: 'var(--silia-fill-content)',
  split: 'var(--silia-split)',

  successBg: 'var(--silia-success-bg)',
  successFg: 'var(--silia-success-fg)',
  successFgStrong: 'var(--silia-success-fg-strong)',
  successBd: 'var(--silia-success-bd)',
  warningBg: 'var(--silia-warning-bg)',
  warningFg: 'var(--silia-warning-fg)',
  warningFgStrong: 'var(--silia-warning-fg-strong)',
  warningBd: 'var(--silia-warning-bd)',
  dangerBg: 'var(--silia-danger-bg)',
  dangerFg: 'var(--silia-danger-fg)',
  dangerFgStrong: 'var(--silia-danger-fg-strong)',
  dangerBd: 'var(--silia-danger-bd)',
  infoBg: 'var(--silia-info-bg)',
  infoFg: 'var(--silia-info-fg)',
  infoFgStrong: 'var(--silia-info-fg-strong)',
  infoBd: 'var(--silia-info-bd)',

  emotionPositiveBg: 'var(--silia-emotion-positive-bg)',
  emotionPositiveFg: 'var(--silia-emotion-positive-fg)',
  emotionNeutralBg: 'var(--silia-emotion-neutral-bg)',
  emotionNeutralFg: 'var(--silia-emotion-neutral-fg)',
  emotionNegativeBg: 'var(--silia-emotion-negative-bg)',
  emotionNegativeFg: 'var(--silia-emotion-negative-fg)',

  kindFolderBg: 'var(--silia-kind-folder-bg)',
  kindFolderFg: 'var(--silia-kind-folder-fg)',
  kindFolderBd: 'var(--silia-kind-folder-bd)',
  kindAgentBg: 'var(--silia-kind-agent-bg)',
  kindAgentFg: 'var(--silia-kind-agent-fg)',
  kindAgentBd: 'var(--silia-kind-agent-bd)',
  kindAgentSoftBg: 'var(--silia-kind-agent-soft-bg)',
  kindAgentSoftFg: 'var(--silia-kind-agent-soft-fg)',
  kindAgentSoftBd: 'var(--silia-kind-agent-soft-bd)',
  kindQaBg: 'var(--silia-kind-qa-bg)',
  kindQaFg: 'var(--silia-kind-qa-fg)',
  kindQaBd: 'var(--silia-kind-qa-bd)',
  kindRtaBg: 'var(--silia-kind-rta-bg)',
  kindRtaFg: 'var(--silia-kind-rta-fg)',
  kindRtaBd: 'var(--silia-kind-rta-bd)',
  kindDataViewBg: 'var(--silia-kind-data-view-bg)',
  kindDataViewFg: 'var(--silia-kind-data-view-fg)',
  kindDataViewBd: 'var(--silia-kind-data-view-bd)',
  kindDashboardBg: 'var(--silia-kind-dashboard-bg)',
  kindDashboardFg: 'var(--silia-kind-dashboard-fg)',
  kindDashboardBd: 'var(--silia-kind-dashboard-bd)',
  kindFileBg: 'var(--silia-kind-file-bg)',
  kindFileFg: 'var(--silia-kind-file-fg)',
  kindFileBd: 'var(--silia-kind-file-bd)',

  nodeToolBg: 'var(--silia-node-tool-bg)',
  nodeToolFg: 'var(--silia-node-tool-fg)',
  nodeToolBd: 'var(--silia-node-tool-bd)',
  nodeMcpBg: 'var(--silia-node-mcp-bg)',
  nodeMcpFg: 'var(--silia-node-mcp-fg)',
  nodeMcpBd: 'var(--silia-node-mcp-bd)',
  nodeTableBg: 'var(--silia-node-table-bg)',
  nodeTableFg: 'var(--silia-node-table-fg)',
  nodeTableBd: 'var(--silia-node-table-bd)',
  nodeAgentBg: 'var(--silia-node-agent-bg)',
  nodeAgentFg: 'var(--silia-node-agent-fg)',
  nodeAgentBd: 'var(--silia-node-agent-bd)',

  fill1: 'var(--silia-fill-1)',
  fill2: 'var(--silia-fill-2)',
  fill3: 'var(--silia-fill-3)',
  fillBrand1: 'var(--silia-fill-brand-1)',
  fillBrand2: 'var(--silia-fill-brand-2)',
  fillInverse1: 'var(--silia-fill-inverse-1)',
  fillInverse2: 'var(--silia-fill-inverse-2)',
  scrim: 'var(--silia-scrim)',
  scrimStrong: 'var(--silia-scrim-strong)',
  limeSoft: 'var(--silia-primary-bg-subtle)',
  limeBorder: 'var(--silia-primary-border)',
  limeTenue: 'var(--silia-primary-bg)',

  shadowSm: 'var(--silia-shadow-sm)',
  shadowMd: 'var(--silia-shadow-md)',
  shadowLg: 'var(--silia-shadow-lg)',
  shadowXl: 'var(--silia-shadow-xl)',
  focusRing: 'var(--silia-focus-ring)',
  shadowGlow: 'var(--silia-shadow-glow)',
  shadowCta: 'var(--silia-shadow-cta)',
  shadowCtaHover: 'var(--silia-shadow-cta-hover)',

  fontSans: 'var(--silia-font-sans)',
  fontMono: 'var(--silia-font-mono)',

  /* Shape and space as `var()` strings too, so a template can retune them —
     Traxión's radius base is 6, not 12. */
  radiusXs: 'var(--silia-radius-xs)',
  radiusSm: 'var(--silia-radius-sm)',
  radiusMd: 'var(--silia-radius-md)',
  radiusLg: 'var(--silia-radius-lg)',
  radiusPill: 'var(--silia-radius-pill)',
  radiusCircle: 'var(--silia-radius-circle)',
  spaceXxs: 'var(--silia-space-xxs)',
  spaceXs: 'var(--silia-space-xs)',
  spaceSm: 'var(--silia-space-sm)',
  spaceMd: 'var(--silia-space-md)',
  spaceLg: 'var(--silia-space-lg)',
  spaceXl: 'var(--silia-space-xl)',
  spaceXxl: 'var(--silia-space-xxl)',
  controlHeight: 'var(--silia-ctl-height)',
  controlHeightSm: 'var(--silia-ctl-height-sm)',
} as const;
